import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import PortalApiService from '../../../services/PortalApiService';
import Loader from '../../../components/Loader';
import Paginator from '../../../components/Paginator';
import PageHeader from '../../../components/ui/PageHeader';
import StatusBadge from '../../../components/ui/StatusBadge';
import Avatar from '../../../components/ui/Avatar';
import { BOOKING_STATUSES } from '../../../assets/utils/statusColors';
import { formatDate } from '../../../utils/helper';

const currency = process.env.REACT_APP_CURRENCY || 'AED';

// Labels for the action a provider takes to reach a status
export const ACTION_LABELS = {
    Confirmed: { label: 'Confirm', icon: 'bi-check2', className: 'btn-primary' },
    'In-Progress': { label: 'Start', icon: 'bi-play-fill', className: 'btn-light-primary' },
    Completed: { label: 'Complete', icon: 'bi-check2-all', className: 'btn-primary' },
    Cancelled: { label: 'Cancel', icon: 'bi-x-lg', className: 'hp-btn-outline text-danger' },
};

export const confirmStatusChange = async (booking, status) => {
    const result = await Swal.fire({
        title: `${ACTION_LABELS[status]?.label || 'Update'} booking #${booking.job_code}?`,
        text: status === 'Cancelled'
            ? 'The customer will be notified that this booking is cancelled.'
            : `The booking will be marked as ${status} and the customer notified.`,
        icon: status === 'Cancelled' ? 'warning' : 'question',
        showCancelButton: true,
        confirmButtonText: `Yes, ${(ACTION_LABELS[status]?.label || 'update').toLowerCase()}`,
        confirmButtonColor: status === 'Cancelled' ? '#e5484d' : '#03b463',
        cancelButtonText: 'Not now',
    });
    if (!result.isConfirmed) return null;
    const { data } = await PortalApiService.request({
        method: 'POST',
        url: `/bookings/${booking._id}/status`,
        data: { status },
    });
    if (data.status) {
        toast.success(data.message);
        return data.data;
    }
    toast.error(data.message);
    return null;
};

const PortalBookings = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [pagination, setPagination] = useState({});
    const [isLoader, setIsLoader] = useState(false);
    const [apiData, setApiData] = useState({ search: '', status: searchParams.get('status') || '', page: 1 });

    const fetchData = async () => {
        try {
            setIsLoader(true);
            const { data } = await PortalApiService.request({ method: 'GET', url: '/bookings', data: apiData });
            if (data.status) {
                setItems(data.data);
                setCounts(data.counts || {});
                setPagination(data.pagination || {});
            }
        } catch (error) {
            // PortalApiService already shows the error toast
        } finally {
            setIsLoader(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [apiData.page, apiData.search, apiData.status]);

    const setStatus = (status) => {
        setApiData(prev => ({ ...prev, status, page: 1 }));
        setSearchParams(status ? { status } : {});
    };

    const changeStatus = async (booking, status) => {
        try {
            const updated = await confirmStatusChange(booking, status);
            if (updated) fetchData();
        } catch (error) {
            // PortalApiService already shows the error toast
        }
    };

    const tabs = [
        { key: '', label: 'All', count: counts.all },
        ...BOOKING_STATUSES.map(s => ({ key: s, label: s, count: counts[s] })),
    ];
    if (apiData.status === 'Active') {
        tabs.splice(1, 0, { key: 'Active', label: 'Active', count: (counts.Confirmed || 0) + (counts['In-Progress'] || 0) });
    }

    return (
        <div className="hp-page">
            <PageHeader title="Bookings" subtitle="Confirm new requests, start jobs and mark them complete." />

            <div className="hp-card">
                <div className="hp-card-body d-flex flex-wrap align-items-center gap-3 pb-0">
                    <div className="hp-segment flex-grow-1" role="tablist" aria-label="Filter by status">
                        {tabs.map(t => (
                            <button key={t.key} type="button" role="tab" aria-selected={apiData.status === t.key}
                                className={apiData.status === t.key ? 'active' : ''} onClick={() => setStatus(t.key)}>
                                {t.label}<span className="hp-count">{t.count || 0}</span>
                            </button>
                        ))}
                    </div>
                    <div className="hp-header-search" style={{ flex: '0 1 280px' }}>
                        <i className="bi bi-search"></i>
                        <input type="search" value={apiData.search} placeholder="Search code, customer…" aria-label="Search bookings"
                            onChange={e => setApiData({ ...apiData, search: e.target.value, page: 1 })} />
                    </div>
                </div>

                <div className="table-responsive mt-4">
                    <table className="table hp-table table-hover align-middle mb-0">
                        <thead>
                            <tr>
                                <th>Booking</th>
                                <th>Customer</th>
                                <th>Scheduled</th>
                                <th>Location</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length > 0 ? items.map(b => (
                                <tr key={b._id}>
                                    <td>
                                        <Link to={`/bookings/${b._id}`} className="fw-bold text-dark text-hover-primary">#{b.job_code}</Link>
                                        <div className="text-muted fs-7">{b.service_id?.name || '—'}</div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center gap-2">
                                            <Avatar src={b.user_id?.picture} name={b.user_id?.name} size={30} />
                                            <div>
                                                <div className="text-dark">{b.user_id?.name || '—'}</div>
                                                <div className="text-muted fs-8">{b.user_id?.phone_code} {b.user_id?.phone}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="text-nowrap">
                                        {b.date ? formatDate(b.date, 'DD MMM YYYY') : '—'}
                                        {b.time && <div className="text-muted fs-7">{b.time}</div>}
                                    </td>
                                    <td style={{ maxWidth: 220 }}>
                                        <span className="d-block text-truncate">
                                            {b.property_id ? `${b.property_id.building_name}, Unit ${b.property_id.unit_no}` : (b.location || '—')}
                                        </span>
                                        {b.property_id?.area && <span className="d-block text-muted fs-7 text-truncate">{b.property_id.area}</span>}
                                    </td>
                                    <td className="text-nowrap">{b.pay_amount != null ? `${currency} ${Number(b.pay_amount).toFixed(2)}` : '—'}</td>
                                    <td><StatusBadge status={b.status} /></td>
                                    <td className="text-end text-nowrap">
                                        {(b.next_statuses || []).filter(s => s !== 'Cancelled').map(s => (
                                            <button key={s} type="button" className={`btn btn-sm ${ACTION_LABELS[s]?.className} me-1`} onClick={() => changeStatus(b, s)}>
                                                <i className={`bi ${ACTION_LABELS[s]?.icon} me-1`}></i>{ACTION_LABELS[s]?.label}
                                            </button>
                                        ))}
                                        <Link to={`/bookings/${b._id}`} className="btn btn-sm hp-btn-outline" title="View booking" aria-label={`View booking ${b.job_code}`}>
                                            <i className="bi bi-eye"></i>
                                        </Link>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="7">
                                        {!isLoader && (
                                            <div className="hp-empty">
                                                <i className="bi bi-calendar2-x"></i>
                                                {apiData.status || apiData.search ? 'No bookings match these filters.' : 'No bookings yet. They will show up here when customers book your services.'}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {isLoader && <div className="py-6"><Loader text="Loading" size="30" /></div>}
                {pagination.total_pages > 1 && (
                    <div className="hp-card-body d-flex justify-content-between align-items-center">
                        <span className="text-muted fs-7">{pagination.total_records} bookings</span>
                        <Paginator page={pagination.current_page} rows={pagination.per_page} totalRecords={pagination.total_records}
                            onPageChange={page => setApiData(prev => ({ ...prev, page }))} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default PortalBookings;
